import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AiWriter } from "@/components/admin/AiWriter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("spf_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("all"); // all | application | lead | newsletter
  const [view, setView] = useState("submissions"); // submissions | writer
  const [smsOnly, setSmsOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      setToken(r.data.token);
      localStorage.setItem("spf_admin_token", r.data.token);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Token": token } });
      setItems(r.data.submissions || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("spf_admin_token");
        setToken("");
      } else {
        toast.error("Failed to load");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const counts = useMemo(() => {
    const c = { all: items.length, application: 0, lead: 0, contact: 0, newsletter: 0, sms: 0 };
    items.forEach((r) => {
      if (c[r.kind] !== undefined) c[r.kind] += 1;
      if (r.kind === "application" && r.data?.sms_optin === true) c.sms += 1;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let rows = tab === "all" ? items : items.filter((r) => r.kind === tab);
    if (smsOnly) rows = rows.filter((r) => r.kind === "application" && r.data?.sms_optin === true);
    return rows;
  }, [items, tab, smsOnly]);

  const previewAutoreply = async (kind) => {
    try {
      const r = await axios.get(`${API}/admin/autoreply-preview`, {
        params: { kind },
        headers: { "X-Admin-Token": token },
      });
      const blob = new Blob([r.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke shortly after so the new tab can still load.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Preview failed. Please try again.");
    }
  };

  const exportNewsletter = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/newsletter/export`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `steadypath-newsletter-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Subscribers exported.");
    } catch (e) {
      toast.error("Export failed. Try again.");
    } finally {
      setExporting(false);
    }
  };

  if (!token) {
    return (
      <div className="pt-40 pb-32">
        <form onSubmit={login} className="max-w-md mx-auto px-6 border border-black/10 bg-[color:var(--bg-elev)] p-10">
          <div className="overline">Admin · Restricted</div>
          <h1 className="mt-4 font-serif text-4xl">Sign in</h1>
          <div className="mt-8">
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">Password</label>
            <input
              data-testid={ADMIN.loginPassword}
              type="password"
              className="editorial-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <button
            data-testid={ADMIN.loginButton}
            disabled={loading}
            type="submit"
            className="mt-8 w-full bg-[color:var(--gold)] text-black px-6 py-3.5 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors disabled:opacity-60"
          >
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </form>
      </div>
    );
  }

  const logout = () => {
    localStorage.removeItem("spf_admin_token");
    setToken("");
  };

  const TABS = [
    { id: "all", label: "All" },
    { id: "application", label: "Applications" },
    { id: "lead", label: "Leads" },
    { id: "contact", label: "Messages" },
    { id: "newsletter", label: "Newsletter" },
  ];

  return (
    <div className="pt-32 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between border-b border-black/10 pb-6 flex-wrap gap-4">
          <div>
            <div className="overline">Admin · Dashboard</div>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl">
              {view === "writer" ? "AI Writer" : `Submissions (${counts.all})`}
            </h1>
            <div className="mt-5 flex gap-2">
              <button
                data-testid="admin-view-submissions"
                onClick={() => setView("submissions")}
                className={`px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors ${view === "submissions" ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]" : "border-[color:var(--bone)]/20 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"}`}
              >
                Submissions
              </button>
              <button
                data-testid="admin-view-writer"
                onClick={() => setView("writer")}
                className={`px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors ${view === "writer" ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]" : "border-[color:var(--bone)]/20 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"}`}
              >
                AI Writer
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={() => previewAutoreply("lead")}
              data-testid="admin-preview-lead-autoreply"
              className="inline-flex items-center gap-2 border border-[color:var(--bone)]/25 text-[color:var(--bone)]/80 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              title="Preview the lead autoreply email"
            >
              Preview lead reply
            </button>
            <button
              onClick={() => previewAutoreply("application")}
              data-testid="admin-preview-application-autoreply"
              className="inline-flex items-center gap-2 border border-[color:var(--bone)]/25 text-[color:var(--bone)]/80 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              title="Preview the application autoreply email"
            >
              Preview app reply
            </button>
            <button
              onClick={exportNewsletter}
              disabled={exporting || counts.newsletter === 0}
              data-testid={ADMIN.exportNewsletter}
              className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={counts.newsletter === 0 ? "No subscribers yet" : "Download subscribers CSV"}
            >
              <Download size={13} />
              {exporting ? "Exporting…" : `Export newsletter (${counts.newsletter})`}
            </button>
            <button onClick={logout} className="font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--bone)]/60 hover:text-[color:var(--gold)]">
              Sign out
            </button>
          </div>
        </div>

        {view === "writer" && <AiWriter api={API} token={token} />}

        {view === "submissions" && (
        <>
        {/* Tabs + SMS filter */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors ${
                tab === t.id
                  ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                  : "border-[color:var(--bone)]/20 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
              }`}
            >
              {t.label} · {counts[t.id]}
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-[color:var(--bone)]/15" />
          <button
            data-testid="admin-filter-sms"
            onClick={() => setSmsOnly((v) => !v)}
            className={`px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors ${
              smsOnly
                ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                : "border-[color:var(--bone)]/20 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
            }`}
            title="Applications where the applicant opted in to SMS updates"
          >
            SMS opt-ins · {counts.sms}
          </button>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table data-testid={ADMIN.submissionsTable} className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50 border-b border-black/10">
                <th className="py-4 pr-6">Received</th>
                <th className="py-4 pr-6">Type</th>
                <th className="py-4 pr-6">Name</th>
                <th className="py-4 pr-6">Business</th>
                <th className="py-4 pr-6">Amount</th>
                <th className="py-4 pr-6">Email</th>
                <th className="py-4 pr-6">Mobile</th>
                <th className="py-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  data-testid={ADMIN.submissionRow}
                  className="border-b border-black/[0.06] hover:bg-black/[0.03] transition-colors"
                >
                  <td className="py-4 pr-6 font-mono text-xs text-[color:var(--bone)]/70">
                    {new Date(row.created_at).toLocaleString()}
                  </td>
                  <td className="py-4 pr-6">
                    <span className={`font-mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border ${row.kind === "application" ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-black/25 text-[color:var(--bone)]/80"}`}>
                      {row.kind}
                    </span>
                    {row.kind === "application" && row.data?.sms_optin === true && (
                      <span
                        data-testid="admin-row-sms-badge"
                        className="ml-2 font-mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold)]"
                        title="Applicant opted in to broker text updates"
                      >
                        SMS ✓
                      </span>
                    )}
                  </td>
                  <td className="py-4 pr-6">
                    {[row.data.first_name, row.data.last_name].filter(Boolean).join(" ") || row.data.full_name || "—"}
                  </td>
                  <td className="py-4 pr-6">{row.data.business_name || "—"}</td>
                  <td className="py-4 pr-6 font-mono">{row.data.funding_amount || "—"}</td>
                  <td className="py-4 pr-6 text-[color:var(--bone)]/80">{row.data.email}</td>
                  <td className="py-4 pr-6 text-[color:var(--bone)]/80">
                    {row.data.mobile_phone || row.data.phone || "—"}
                  </td>
                  <td className="py-4">
                    <button onClick={() => setSelected(row)} className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--gold)] gold-link">
                      View →
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-16 text-center text-[color:var(--bone)]/50 font-mono text-xs tracking-[0.22em] uppercase">
                    No submissions in this view
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-[color:var(--bg-elev)] border border-black/10 text-[color:var(--bone)]">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              {selected?.data?.business_name ||
                [selected?.data?.first_name, selected?.data?.last_name].filter(Boolean).join(" ") ||
                selected?.data?.full_name ||
                selected?.data?.email}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4">
              {Object.entries(selected.data).map(([k, v]) => (
                <div key={k} className="border-b border-black/10 py-2">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {k.replace(/_/g, " ")}
                  </div>
                  <div className="mt-1">{v || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
